import { Badge } from "@/components/ui/Badge";
import { useAnalytics } from "@/features/analytics/context/AnalyticsProvider";
import type { CitationSourceCta } from "@/features/analytics/types/events";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  usePaperFiguresQuery,
  usePaperMetaQuery,
  usePaperQuery,
} from "../api/queries";
import { ArxivPdfViewer } from "../components/ArxivPdfViewer";
import { ContextExpander } from "../components/ContextExpander";
import { FigureImage } from "../components/FigureImage";
import { MarkdownLine } from "../components/MarkdownLine";
import { TableBlock } from "../components/TableBlock";
import { useTrackCitationView } from "../hooks/useTrackCitationView";
import type { ClassifiedLine, FigureInfo, PaperMeta } from "../types";
import { HEADER_ROLES } from "../types";
import { classifyLine } from "../utils/classifyLine";
import { formatAuthors } from "../utils/formatAuthors";
import { groupTableLines } from "../utils/groupTableLines";
import { getExternalUrlFromId, getOriginalPaperUrl } from "../utils/originalUrl";
import { parseHighlights } from "../utils/parseHighlights";
import { scrollToTarget } from "../utils/scrollToTarget";
import { segmentLines } from "../utils/segmentLines";


function getSourceLabelFromId(docId: string): string {
  if (docId.startsWith("bio_")) return "bioRxiv";
  if (docId.startsWith("med_")) return "medRxiv";
  if (docId.startsWith("arx_") || docId.startsWith("arxiv_")) return "arXiv";
  if (docId.toUpperCase().startsWith("PMC")) return "PubMed Central";
  return "source";
}


function getSourceLabel(meta: PaperMeta): string {
  switch (meta.source?.toLowerCase()) {
    case "arxiv": return "arXiv";
    case "pmc": return "PubMed Central";
    case "biorxiv": return "bioRxiv";
    case "medrxiv": return "medRxiv";
    default: return meta.source || "source";
  }
}


interface PaperViewerPageProps {
  docIdProp?: string;
  highlightsProp?: Set<number>;
}

export const PaperViewerPage = ({ docIdProp, highlightsProp }: PaperViewerPageProps = {}) => {
  const { docId: routeDocId } = useParams<{ docId: string }>();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const firstHighlightRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const docId = docIdProp ?? routeDocId;
  const highlights = useMemo(
    () => highlightsProp ?? parseHighlights(location.hash),
    [highlightsProp, location.hash]
  );

  const { data: earlyMeta } = usePaperMetaQuery(docId);
  const {
    data,
    isPending: loading,
    error: paperError,
  } = usePaperQuery(docId);
  const { data: figures = [] } = usePaperFiguresQuery(docId);
  const error = paperError instanceof Error ? paperError.message : paperError ? String(paperError) : null;

  const matchedHighlightCount = useMemo(() => {
    if (!data) return 0;
    return data.lines.filter((l) => highlights.has(l.num)).length;
  }, [data, highlights]);

  const hasCitations = matchedHighlightCount > 0;

  useEffect(() => {
    if (data && firstHighlightRef.current && scrollContainerRef.current) {
      setTimeout(() => {
        scrollToTarget(scrollContainerRef.current, firstHighlightRef.current);
      }, 100);
    }
  }, [data, highlights]);

  useTrackCitationView("papers", docId, data ?? null, highlights.size);

  const figureAtLine = useMemo(() => {
    const map = new Map<number, FigureInfo>();
    const byFigNum = new Map<number, FigureInfo>();
    for (const fig of figures) {
      if (fig.line_num) map.set(fig.line_num, fig);
      if (fig.figure_num) byFigNum.set(fig.figure_num, fig);
    }
    return { byLine: map, byFigNum };
  }, [figures]);

  const classified: ClassifiedLine[] = useMemo(() => {
    if (!data) return [];
    const title = data.meta.title || "";
    return data.lines.map((line, i) => ({
      ...line,
      role: classifyLine(line, i, data.lines, title),
    }));
  }, [data]);

  const segments = useMemo(
    () => (hasCitations ? segmentLines(classified, highlights) : []),
    [classified, highlights, hasCitations]
  );

  const firstHighlight = useMemo(
    () => [...highlights].sort((a, b) => a - b)[0],
    [highlights]
  );

  const renderLine = (line: ClassifiedLine, highlighted: boolean): React.ReactNode => {
    const isFirst = highlighted && line.num === firstHighlight;
    let figure = figureAtLine.byLine.get(line.num);
    if (!figure && (line.role === "figure_caption" || line.role === "table_caption")) {
      const numMatch = line.text.match(/^(?:Figure|Fig\.?|Table)\s+(\d+)/i);
      if (numMatch) figure = figureAtLine.byFigNum.get(parseInt(numMatch[1]));
    }

    const highlightClasses = highlighted
      ? "bg-amber-50 border-l-[3px] border-l-amber-400 pl-4 -ml-4 rounded-r"
      : "";

    switch (line.role) {
      case "major_heading":
        return (
          <h2
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-2xl font-medium tracking-tight text-zinc-900 mt-10 mb-3 pb-1.5 border-b border-zinc-200 ${highlightClasses}`}
          >
            {line.text}
          </h2>
        );

      case "minor_heading":
        return (
          <h3
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base font-medium text-zinc-800 mt-7 mb-2 ${highlightClasses}`}
          >
            {line.text}
          </h3>
        );

      case "figure_caption":
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`my-4 ${highlightClasses}`}
          >
            {figure && <FigureImage src={figure.url} alt={`Figure ${figure.figure_num || ""}`} />}
            <p className="text-sm text-zinc-500 leading-relaxed italic px-4">
              <span className="font-semibold not-italic text-zinc-700">
                {line.text.match(/^((?:Figure|Fig\.?)\s+\d+)\s*[:.]?\s*/i)?.[1] || "Figure"}:
              </span>{" "}
              {line.text.replace(/^(?:Figure|Fig\.?)\s+\d+\s*[:.]?\s*/i, "")}
            </p>
          </div>
        );

      case "table_caption":
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`my-4 ${highlightClasses}`}
          >
            {figure && <FigureImage src={figure.url} alt={`Table ${figure.table_num || ""}`} />}
            <p className="text-sm text-zinc-600 leading-relaxed px-4">
              <span className="font-semibold text-zinc-800">
                {line.text.match(/^(Table\s+\d+)\s*[:.]?\s*/i)?.[1] || "Table"}:
              </span>{" "}
              {line.text.replace(/^Table\s+\d+\s*[:.]?\s*/i, "")}
            </p>
          </div>
        );

      case "reference":
        return (
          <p
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-sm text-zinc-500 leading-relaxed pl-6 -indent-6 mb-1 ${highlightClasses}`}
          >
            {line.text}
          </p>
        );

      case "abstract":
        return (
          <p
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base text-zinc-600 leading-relaxed mb-3 italic ${highlightClasses}`}
          >
            {line.text}
          </p>
        );

      default:
        return (
          <div
            id={`L${line.num}`}
            ref={isFirst ? firstHighlightRef : undefined}
            className={`text-base text-zinc-800 leading-[1.7] mb-3 ${highlightClasses}`}
          >
            <MarkdownLine content={line.text} />
          </div>
        );
    }
  };

  const renderGroupedLines = (lines: ClassifiedLine[], highlighted: boolean): React.ReactNode[] => {
    const groups = groupTableLines(lines);
    return groups.map((group) => {
      if (group.type === "table") {
        return (
          <TableBlock
            key={`tbl-${group.lines[0].num}`}
            lines={group.lines}
            highlightedNums={highlighted ? highlights : undefined}
            firstHighlightNum={firstHighlight}
            firstHighlightRef={firstHighlightRef}
          />
        );
      }
      return (
        <div key={group.line.num}>{renderLine(group.line as ClassifiedLine, highlighted)}</div>
      );
    });
  };

  if (loading) {
    if (earlyMeta?.title) {
      const earlyAuthorStr = formatAuthors(earlyMeta.authors);
      const earlySource = earlyMeta.source?.toUpperCase() || "";
      return (
        <div className="fixed inset-0 overflow-y-auto bg-white">
          <div className="max-w-3xl mx-auto px-6 pt-16 pb-20">
            {earlySource && (
              <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-medium text-3xs uppercase tracking-wide">
                {earlySource}
              </span>
            )}
            <h1 className="font-display text-4xl font-normal text-zinc-900 leading-[1.15] mb-4 mt-4 tracking-tight">
              {earlyMeta.title}
            </h1>
            {earlyAuthorStr && (
              <p className="text-base text-zinc-600 leading-relaxed mb-1">{earlyAuthorStr}</p>
            )}
            {earlyMeta.journal && (
              <p className="text-xs text-zinc-400 mb-1 italic">{earlyMeta.journal}</p>
            )}
            {earlyMeta.month_year && (
              <p className="text-xs text-zinc-400 mb-4">{earlyMeta.month_year?.replace("_", " ")}</p>
            )}
            {earlyMeta.abstract && (
              <p className="text-sm text-zinc-700 leading-[1.75] mb-6 italic">{earlyMeta.abstract}</p>
            )}
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              Loading full text…
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-zinc-400 text-sm">Loading paper…</div>
      </div>
    );
  }

  if (!data) {
    const externalUrl = docId ? getExternalUrlFromId(docId) : null;
    const notFound = error === "Paper not found";
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-zinc-900 text-lg font-semibold mb-2">
            {notFound ? "Paper not found" : "Failed to load paper"}
          </div>
          <div className="text-zinc-500 text-sm mb-4">{error || "The requested paper could not be loaded."}</div>
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              View on {getSourceLabelFromId(docId || "")}
            </a>
          )}
          <div className="text-zinc-400 text-xs mt-4 font-mono">{docId}</div>
        </div>
      </div>
    );
  }

  const { meta } = data;
  const authorStr = formatAuthors(meta.authors);
  const source = meta.source?.toUpperCase() || "";
  const isArxiv = meta.source?.toLowerCase() === "arxiv";
  const originalUrl = getOriginalPaperUrl(meta, docId || "");
  const sourceLabel = getSourceLabel(meta);

  const trackSourceClick = (cta: CitationSourceCta) => {
    if (!docId || !originalUrl) return;
    trackEvent("citation_source_click", {
      citation_route: "papers",
      doc_id: docId,
      source_label: sourceLabel,
      cta,
      link_url: originalUrl,
      ...(user?.id && { gxl_user_id: user.id }),
    });
  };

  if (!data.lines || data.lines.length === 0) {
    return (
      <div className="fixed inset-0 overflow-y-auto bg-white">
        <div className="max-w-3xl mx-auto px-6 pt-16 pb-20">
          {source && (
            <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-medium text-3xs uppercase tracking-wide">
              {source}
            </span>
          )}
          <h1 className="font-display text-4xl font-normal text-zinc-900 leading-[1.15] mb-4 mt-4 tracking-tight">
            {meta.title || docId}
          </h1>
          {authorStr && (
            <p className="text-base text-zinc-600 leading-relaxed mb-1">{authorStr}</p>
          )}
          {meta.month_year && (
            <p className="text-xs text-zinc-400 mb-4">{meta.month_year}</p>
          )}
          {meta.abstract && (
            <p className="text-sm text-zinc-700 leading-[1.75] mb-6 italic">{meta.abstract}</p>
          )}
          <div className="border border-zinc-200 rounded-[3px] p-6 text-center">
            <p className="text-zinc-500 text-sm mb-4">Full text is not available in the viewer for this paper.</p>
            {originalUrl && (
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSourceClick("header")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-700 transition-colors"
              >
                View on {sourceLabel}
              </a>
            )}
          </div>
          {meta.doi && (
            <div className="mt-4 text-xs text-zinc-400">
              <a href={`https://doi.org/${meta.doi}`} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-600 underline">
                doi:{meta.doi}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} data-scroll-container className="fixed inset-0 overflow-y-auto bg-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <a href="/" className="text-sm font-semibold text-zinc-900 hover:text-zinc-600 transition-colors">
            gxl
          </a>
          <div className="flex items-center gap-3 text-xs text-zinc-400">
            {source && (
              <Badge variant="default" className="text-2xs pt-1 pb-0.25">{source}</Badge>
            )}
            <span className="font-mono">{docId}</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pt-10 pb-2">
        {originalUrl && (
          <a
            href={originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSourceClick("header")}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 mb-4 transition-colors"
          >
            View on {sourceLabel}
          </a>
        )}
        <h1 className="font-display text-4xl font-normal text-zinc-900 leading-[1.15] mb-4 tracking-tight">
          {meta.title || docId}
        </h1>
        {authorStr && (
          <p className="text-base text-zinc-600 leading-relaxed mb-1">
            {authorStr}
          </p>
        )}
        {meta.month_year && (
          <p className="text-xs text-zinc-400 mb-1">{meta.month_year}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400 mt-2 mb-6">
          {meta.journal && <span>{meta.journal}</span>}
          {meta.doi && (
            <a
              href={`https://doi.org/${meta.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-zinc-800 underline decoration-zinc-300"
            >
              doi:{meta.doi}
            </a>
          )}
          {meta.pmid && (
            <a
              href={`https://pubmed.ncbi.nlm.nih.gov/${meta.pmid}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-zinc-800 underline decoration-zinc-300"
            >
              PMID:{meta.pmid}
            </a>
          )}
        </div>
        {hasCitations && (
          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mb-4 inline-block">
            {matchedHighlightCount} line{matchedHighlightCount !== 1 ? "s" : ""} cited
          </div>
        )}
      </div>

      <article className="max-w-3xl mx-auto px-6 pb-20">
        {isArxiv && hasCitations ? (
          <ArxivPdfViewer docId={docId || ""} highlights={highlights} />
        ) : isArxiv && !hasCitations ? (
          <div className="text-center py-12">
            {originalUrl && (
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSourceClick("arxiv_fallback")}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-[3px] bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-700 transition-colors"
              >
                View full article on arXiv
              </a>
            )}
          </div>
        ) : hasCitations
          ? segments.map((segment, idx) =>
            segment.type === "cited" ? (
              <div key={idx}>
                {groupTableLines(segment.lines).map((group) =>
                  group.type === "table" ? (
                    <div key={`tbl-${group.lines[0].num}`} className="relative">
                      <span className="absolute -left-20 top-[2px] select-none text-xs font-mono font-medium text-amber-500 whitespace-nowrap">
                        Line {group.lines[0].num}:
                      </span>
                      <TableBlock
                        lines={group.lines}
                        highlightedNums={highlights}
                        firstHighlightNum={firstHighlight}
                        firstHighlightRef={firstHighlightRef}
                      />
                    </div>
                  ) : (
                    <div key={group.line.num} className="relative">
                      <span className="absolute -left-20 top-[2px] select-none text-xs font-mono font-medium text-amber-500 whitespace-nowrap">
                        Line {group.line.num}:
                      </span>
                      {renderLine(group.line as ClassifiedLine, true)}
                    </div>
                  )
                )}
              </div>
            ) : (
              <ContextExpander
                key={idx}
                lines={segment.lines}
                onRenderLines={(ctxLines) => renderGroupedLines(ctxLines, false)}
              />
            )
          )
          : renderGroupedLines(
            classified.filter((l) => !HEADER_ROLES.has(l.role)),
            false
          )}
      </article>
    </div>
  );
};
